/**
 * Numbers, money and dates, formatted for whichever language is on.
 *
 * German writes 1.234,56 € and 07.08.2026; English writes €1,234.56 and
 * 7 Aug 2026. Getting this wrong in a finance table is not cosmetic -- a
 * German reader seeing "4,900.00" reads four euros ninety.
 */

const LOCALES: Record<string, string> = { de: 'de-DE', en: 'en-GB' };

function locale(language: string): string {
  return LOCALES[language] ?? LOCALES.de;
}

export function money(cents: number, currency: string, language: string): string {
  return new Intl.NumberFormat(locale(language), {
    style: 'currency',
    currency: currency || 'EUR',
  }).format(cents / 100);
}

export function count(value: number, language: string): string {
  return new Intl.NumberFormat(locale(language)).format(value);
}

export function decimal(value: number, language: string): string {
  return new Intl.NumberFormat(locale(language), {
    maximumFractionDigits: 3,
  }).format(value);
}

export function date(value: string | null | undefined, language: string): string | null {
  if (!value) return null;
  const parsed = parse(value);
  if (!parsed) return null;
  return new Intl.DateTimeFormat(locale(language), {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  }).format(parsed);
}

/** A YYYY-MM-DD calendar value has no timezone and must not move a day. */
export function calendarDate(value: string | null | undefined, language: string): string | null {
  if (!value || !/^\d{4}-\d{2}-\d{2}$/.test(value)) return null;
  const parsed = new Date(`${value}T00:00:00Z`);
  if (Number.isNaN(parsed.getTime())) return null;
  return new Intl.DateTimeFormat(locale(language), { day: '2-digit', month: 'short', year: 'numeric', timeZone: 'UTC' }).format(parsed);
}

export function billingDate(value: string | null | undefined, language: string): string | null {
  if (!value) return null;
  const parsed = parse(value);
  if (!parsed) return null;
  return new Intl.DateTimeFormat(locale(language), {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    timeZone: 'Europe/Berlin',
  }).format(parsed);
}

export function berlinDateTime(value: string | null | undefined, language: string): string | null {
  if (!value) return null;
  const parsed = parse(value);
  if (!parsed) return null;
  return new Intl.DateTimeFormat(locale(language), {
    day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
    timeZone: 'Europe/Berlin',
  }).format(parsed);
}

export function dateTime(
  value: string | null | undefined,
  language: string,
): string | null {
  if (!value) return null;
  const parsed = parse(value);
  if (!parsed) return null;
  return new Intl.DateTimeFormat(locale(language), {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(parsed);
}

/**
 * "vor 3 Tagen" / "3 days ago" for anything recent.
 *
 * Recency is what the activity column is about, and a reader works out
 * "3 days" far faster than they work out the gap between two dates. Older than
 * a month it switches to the date, because "vor 274 Tagen" means nothing.
 */
export function relativeDays(
  value: string | null | undefined,
  language: string,
): string | null {
  if (!value) return null;
  const parsed = parse(value);
  if (!parsed) return null;
  const days = Math.round((Date.now() - parsed.getTime()) / 86_400_000);
  if (days > 30) return billingDate(value, language);
  const relative = new Intl.RelativeTimeFormat(locale(language), { numeric: 'auto' });
  if (days < 1) {
    const hours = Math.round((Date.now() - parsed.getTime()) / 3_600_000);
    return relative.format(-Math.max(hours, 0), 'hour');
  }
  return relative.format(-days, 'day');
}

/**
 * The API sends naive UTC timestamps -- no offset, no trailing Z. Parsed as
 * written they would be read as local time, which puts every activity band an
 * hour or more out.
 */
function parse(value: string): Date | null {
  const normalised = /[Zz]|[+-]\d{2}:?\d{2}$/.test(value) ? value : `${value}Z`;
  const parsed = new Date(normalised);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

export function countryName(code: string | null, language: string): string | null {
  if (!code) return null;
  try {
    const names = new Intl.DisplayNames([locale(language)], { type: 'region' });
    return names.of(code.toUpperCase()) ?? code;
  } catch {
    return code;
  }
}

/** 🇩🇪 from "DE". Decorative only -- the name is always next to it. */
export function countryFlag(code: string | null): string {
  if (!code || code.length !== 2) return '';
  const base = 0x1f1e6;
  return String.fromCodePoint(
    ...code
      .toUpperCase()
      .split('')
      .map((character) => base + character.charCodeAt(0) - 65),
  );
}

/** Display labels for the small set of count units emitted by measurement APIs. */
export function measurementUnit(unit: string, language: string): string {
  if (language === 'en') return unit;
  return unit === 'reps' ? 'Wdh.' : unit === 'hits' ? 'Treffer' : unit;
}
